import { useMemo } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import { useFlags } from '@mint-lib/routing-context';

import { AgentType } from '../types/agents.js';

export const AGENT_NAMES = {
  ['VOICE_AGENT']: 'voice_agent',
};

export const useAgents = () => {
  const { t } = useTranslation('@mint-lib/ai-chat');
  const ff = useFlags('@mint/arm', ['aiSidebar']);

  const agents: AgentType[] = useMemo(
    () => [
      {
        agentName: 'ai_assistant',
        label: 'Assistant AI Agent',
        role: 'qualityAssurance',
        desc: t('Coordinates tasks across all agents.'),
      },
      {
        agentName: 'media_planner',
        label: 'Planner AI Agent',
        role: 'mediaPlanner',
        desc: t(
          'Organizes data into actionable advertising plans to ensure campaign consistency and provides suggestions on platforms and budgets',
        ),
        meta: {
          hasPoweredLabel: true,
          isVisibleInAgentWidget: true,
        },
      },
      {
        agentName: 'strategist',
        label: 'Strategist AI Agent',
        role: 'mediaStrategist',
        desc: t(
          'Builds high-level strategies, identifies key audience clusters, and provides recommendations on channels and budgets.',
        ),
        meta: {
          hasPoweredLabel: true,
          isVisibleInAgentWidget: true,
        },
      },
      {
        label: 'Project Manager AI Agent',
        agentName: 'project_manager',
        role: 'projectManager',
        desc: t('Coordinates tasks across all agents.'),
        meta: {
          isVisibleInAgentWidget: true,
        },
      },
      {
        label: 'Supervisor AI Agent',
        agentName: 'supervisor',
        role: 'supervisor',
        desc: t('Oversees agents responses to ensure consistency.'),
        meta: {
          isVisibleInAgentWidget: true,
        },
      },
      {
        label: 'Data Analyst AI Agent',
        agentName: 'data_analyst',
        role: 'dataAnalyst',
        desc: t(
          'Analyzes data and provides insights to help agents make informed decisions.',
        ),
        meta: {
          hasPoweredLabel: true,
          isVisibleInAgentWidget: true,
        },
      },
      {
        label: 'Reporting Specialist AI Agent',
        agentName: 'report_builder_agent',
        role: 'reportingSpecialist',
        desc: t(
          'AI Agent that generates Word and Excel reports  from any conversation or custom prompt.',
        ),
        meta: {
          isVisibleInAgentWidget: true,
        },
      },
      {
        label: 'Voice AI Agent',
        agentName: AGENT_NAMES.VOICE_AGENT,
        role: 'voiceAgent',
        desc: t('Coordinates tasks across all agents.'),
      },
      {
        label: 'Offline Planner AI Agent',
        agentName: 'offline_planner',
        role: 'offlinePlanner',
        desc: t(
          'AI agent that manages offline media planning activities and offer key recommendations.',
        ),
        meta: {
          isVisibleInAgentWidget: true,
        },
      },
      {
        label: 'Optimisation AI Agent',
        agentName: 'optimisation_agent',
        role: 'mediaOptimization',
        desc: t(
          'AI Agent that deep-dives into your recommendations to explain, refine, accept or reject them.',
        ),
        meta: {
          isVisibleInAgentWidget: ff.aiSidebar.enabled,
        },
      },
    ],
    [t, ff],
  );

  const agentListWidgetData = agents.filter(
    (a) => a.meta?.isVisibleInAgentWidget,
  );

  return {
    agents,
    agentListWidgetData,
  };
};
